import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Kpi, StatusBadge, Empty } from "@/components/ui-bits";
import { fmtINR, fmtNum, fmtDate } from "@/lib/format";
import { Buildings, CurrencyInr, ChartLineUp, CheckCircle, WarningCircle, Eye } from "@phosphor-icons/react";

const SUB_COLOR = {
  active: "bg-emerald-100 text-emerald-800",
  trial: "bg-blue-100 text-blue-800",
  past_due: "bg-amber-100 text-amber-800",
  expired: "bg-red-100 text-red-800",
  suspended: "bg-secondary text-muted-foreground",
};

export default function Billing() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/billing/overview").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="label-caps">Loading…</div>;

  return (
    <div data-testid="billing-page">
      <PageHeader title="Billing & Subscriptions" subtitle="Monitor SaaS revenue, subscription status and per-tenant usage" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi label="Monthly Recurring Revenue" value={fmtINR(data.mrr)} accent="text-emerald-600" />
        <Kpi label="Annual Run Rate" value={fmtINR(data.arr)} />
        <Kpi label="Active Subscriptions" value={fmtNum(data.active_subscriptions)} sub={`of ${fmtNum(data.total_tenants)} tenants`} />
        <Kpi label="Total Tenants" value={fmtNum(data.total_tenants)} />
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="label-caps border-b border-border bg-secondary/40">
              <th className="text-left p-3">Tenant</th>
              <th className="text-left p-3">Plan</th>
              <th className="text-right p-3">Monthly Fee</th>
              <th className="text-center p-3">Subscription</th>
              <th className="text-left p-3">Renewal</th>
              <th className="text-left p-3">Usage</th>
              <th className="text-center p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {data.tenants.map((t) => (
              <tr key={t.tenant_id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`billing-row-${t.tenant_id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    {t.logo ? <img src={t.logo} alt="" className="h-7 w-7 rounded-sm object-cover border border-border" />
                      : <div className="h-7 w-7 rounded-sm bg-primary text-white flex items-center justify-center"><Buildings size={14} weight="bold" /></div>}
                    <span className="font-medium">{t.company_name}</span>
                  </div>
                </td>
                <td className="p-3">{t.plan || "—"}</td>
                <td className="p-3 text-right mono font-semibold">{fmtINR(t.monthly_saas_fee)}</td>
                <td className="p-3 text-center">
                  <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-sm capitalize ${SUB_COLOR[t.subscription_status] || "bg-secondary"}`}>
                    {(t.subscription_status || "").replace("_", " ")}
                  </span>
                </td>
                <td className="p-3">
                  <span className={t.renewal_overdue ? "text-destructive font-semibold flex items-center gap-1" : ""}>
                    {t.renewal_overdue && <WarningCircle size={14} weight="fill" />}
                    {t.renewal_date ? fmtDate(t.renewal_date) : "—"}
                  </span>
                </td>
                <td className="p-3 text-xs text-muted-foreground">
                  <div>{fmtNum(t.usage.accounts)}/{fmtNum(t.usage.account_limit)} accounts</div>
                  <div>{fmtNum(t.usage.credits_used)}/{fmtNum(t.usage.communication_credits)} credits • {fmtNum(t.usage.users)} users</div>
                </td>
                <td className="p-3 text-center">
                  <button onClick={() => navigate(`/tenants/${t.tenant_id}/accounts`)} className="text-primary inline-flex items-center gap-1 text-xs font-semibold hover:underline" data-testid={`billing-view-${t.tenant_id}`}>
                    <Eye size={14} weight="bold" /> Accounts
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.tenants.length === 0 && <Empty text="No tenants yet" />}
      </div>
    </div>
  );
}
